import differenceBy from 'lodash/differenceBy'
import difference from 'lodash/difference'

import {
  type ProductResponseBody,
  type ProductFormValues,
  type ProductUpdateRequestBody,
  type BaseProductUpdateRequestBody,
  type SecondaryImageAngles,
  type SecondaryImageAngleMapping,
  type Attribute,
  type ImageAngles,
  type SecondaryImage,
} from 'legacy/routes/catalog/types/product.types'
import { PRIMARY_IMAGE_FIELD_NAME } from 'legacy/routes/catalog/products/product/form-components/ProductImage'
import { keysOfObject } from 'legacy/common/utils'
import { pick } from 'lodash'

const ATTRIBUTE_KEYS: Attribute[] = [
  'alcoholic',
  'organic',
  'kosher',
  'gluten_free',
  'low_fat',
  'fat_free',
  'sugar_free',
  'vegan',
  'vegetarian',
]

const SECONDARY_IMAGE_DEFAULTS: SecondaryImageAngleMapping = {
  angle_left: undefined,
  angle_right: undefined,
  angle_back: undefined,
  angle_top: undefined,
  ingredients: undefined,
  nutrition: undefined,
  supplements: undefined,
  drugs: undefined,
  lifestyle: undefined,
}

export function deserializeProductValues(product: ProductResponseBody): ProductFormValues {
  const attributes = ATTRIBUTE_KEYS.filter((attribute: Attribute) => product[attribute])
  const hasPrimaryImage = product.has_main_image || product.has_retailer_hero_image
  const unitCount = product.unit_count ? product.unit_count.toString() : ''
  const productFormSecondaryImages =
    product.secondary_images?.reduce(
      (acc, image) => ({
        ...acc,
        [image.angle]: image.primary_url,
      }),
      SECONDARY_IMAGE_DEFAULTS
    ) ?? SECONDARY_IMAGE_DEFAULTS

  return {
    classified_type: product.classified_type,
    item_count: product.item_count,
    id: product.id,
    name: product.name || '',
    display_name: product.display_name || '',
    size: product.size || '',
    aisles: product.aisles || [],
    editable: product.editable,
    unit_count: unitCount,
    details: product.details || '',
    primaryImageUrl: hasPrimaryImage ? product.primary_image_url : null,
    secondary_images: productFormSecondaryImages,
    attributes,
    ingredients: product.ingredients || '',
    productCodes: product.product_codes.map(({ code }) => code).join(', '),
    alcohol_by_volume: product.alcohol_by_volume || '',
    is_private_label: product.is_private_label,
    brand_name: product.brand_name,
    brand_id: product.brand_id,
    has_retailer_hero_image: product.has_retailer_hero_image,
    has_main_image: product.has_main_image,
    main_aisle: product.main_aisle,
    product_category_path: product.product_category_path || '',
    retailerReferenceCode: product.retailer_reference_code || '',
  }
}

function createBaseRequestBody(values: ProductFormValues): BaseProductUpdateRequestBody {
  const { editable, is_private_label, details } = values

  return {
    editable,
    is_private_label,
    details,
  }
}

function addPrimaryImage(
  initialValues: ProductFormValues,
  values: ProductFormValues,
  requestBody: BaseProductUpdateRequestBody
) {
  if (values.primaryImageUrl === null || initialValues.primaryImageUrl === values.primaryImageUrl) {
    return requestBody
  }

  return {
    ...requestBody,
    product_image: { remote_image_url: values.primaryImageUrl },
  }
}

function serializeSecondaryImages(secondaryImages: SecondaryImageAngleMapping) {
  return keysOfObject(secondaryImages).reduce((acc: SecondaryImage[], angle) => {
    const angleUrl = secondaryImages[angle]
    if (!angleUrl) return acc

    return [...acc, { angle, primary_url: angleUrl }]
  }, [])
}

function addSecondaryImages(values: ProductFormValues, requestBody: BaseProductUpdateRequestBody) {
  if (!values.secondary_images) {
    return { ...requestBody, secondary_images: [] }
  }

  const updatedSecondaryImages = serializeSecondaryImages(values.secondary_images)

  return {
    ...requestBody,
    secondary_images: updatedSecondaryImages,
  }
}

function addEditableOrRetailerOwnedProperties(
  values: ProductFormValues,
  requestBody: BaseProductUpdateRequestBody
) {
  const { is_private_label, brand_id, brand_name, size, name, unit_count, editable } = values

  // Owning a product means the brand should be updated through the dropdown
  // i.e. sending an id from a pre-fetched list.
  if (editable && is_private_label) {
    return {
      ...requestBody,
      editable: true,
      is_private_label: true,
      brand_id,
      name,
      size,
      unit_count,
    }
  }

  // Not owning a product (with the editable flag true) means
  // this can be edited as free text, and we should not send brand_id.
  if (editable && !is_private_label) {
    return {
      ...requestBody,
      editable: true,
      is_private_label: false,
      brand_name,
      name,
      size,
      unit_count,
    }
  }

  // If neither, don't add extra fields at all.
  return requestBody
}

export function transformFormValuesToRequestBody(
  initialValues: ProductFormValues,
  productFormValues: ProductFormValues
): ProductUpdateRequestBody {
  const requestBody = createBaseRequestBody(productFormValues)

  const requestBodyWithPrimaryImage = addPrimaryImage(initialValues, productFormValues, requestBody)
  const requestBodyWithSecondaryImages = addSecondaryImages(
    productFormValues,
    requestBodyWithPrimaryImage
  )

  const finalRequestBody = addEditableOrRetailerOwnedProperties(
    productFormValues,
    requestBodyWithSecondaryImages
  )

  return finalRequestBody
}

export function extractDeletedImageAnglesFromValues(
  initialValues: ProductFormValues,
  values: ProductFormValues
): SecondaryImageAngles[] {
  return differenceBy(
    serializeSecondaryImages(initialValues.secondary_images),
    serializeSecondaryImages(values.secondary_images),
    'angle'
  ).map(secondaryImage => secondaryImage.angle)
}

// Select only valid valid  secondary image angles from our set
// (Consequently: removes any unexpected angles)
// The reason for this is that sometimes, catalog sends along some image angles that we
// don't know how to handle in the ui (eg. the angle `unknown`, for uncategorized photos).
// In these cases, we just want to ignore them, and avoid surfacing them to the user.
function normalizeSecondaryImageAngles(secondaryImages: {
  [key: string]: string
}): SecondaryImageAngleMapping {
  const validAngles = keysOfObject(SECONDARY_IMAGE_DEFAULTS)

  return pick(secondaryImages, validAngles)
}

function selectImageAngles(values: ProductFormValues): {
  [key in ImageAngles]: string | undefined
} {
  return {
    [PRIMARY_IMAGE_FIELD_NAME]: values.primaryImageUrl || undefined,
    ...normalizeSecondaryImageAngles(values.secondary_images),
  }
}

export function selectInvalidImageAngles(original: ProductFormValues, next: ProductFormValues) {
  const originalImages = selectImageAngles(original)
  const nextImages = selectImageAngles(next)

  const presentImages = keysOfObject(nextImages).filter(angle => Boolean(nextImages[angle]))
  const updatedImages = presentImages.filter(angle => originalImages[angle] !== nextImages[angle])

  // If no present images were updated, then everything is ok
  if (updatedImages.length === 0) return []

  // Otherwise, we returns any stale images that were not updated
  return difference(presentImages, updatedImages)
}
